/**
 * FAQ accordion.
 *
 * Progressive enhancement over the plain `<details>` list rendered by `sections/faq.liquid`:
 * without this file the questions still open, close and deep link, they just do it instantly and
 * without the search field, which is why the field is hidden from browsers with JavaScript off.
 *
 * Matching folds case and curly apostrophes so a customer typing "you're" finds an answer written
 * with a typographic apostrophe. The fold is deliberately length preserving, because the same
 * folded string is used to locate the ranges that get wrapped in <mark> in the original text.
 */

const FAQ_ANIMATION_DURATION = 260;
const FAQ_SEARCH_DEBOUNCE = 150;
const FAQ_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';

/** Length preserving fold, so indices found here still address the original string. */
function faqFold(value) {
  return String(value || '')
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .toLowerCase();
}

function faqCollapseSpace(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function faqEscape(value) {
  return String(value).replace(
    /[&<>"]/g,
    (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[character]
  );
}

/** Ranges of `terms` inside `text`, merged so overlapping hits produce one <mark>. */
function faqMatchRanges(text, terms) {
  const folded = faqFold(text);
  // A locale where lowercasing changes length would break the index maths below. Rare, but the
  // honest response is to skip highlighting rather than to slice the string in the wrong places.
  if (folded.length !== text.length) return [];

  const ranges = [];
  terms.forEach((term) => {
    let from = 0;
    while (from <= folded.length - term.length) {
      const at = folded.indexOf(term, from);
      if (at === -1) break;
      ranges.push([at, at + term.length]);
      from = at + term.length;
    }
  });

  ranges.sort((a, b) => a[0] - b[0]);
  return ranges.reduce((merged, range) => {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
    } else {
      merged.push(range.slice());
    }
    return merged;
  }, []);
}

class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.blocks = this.querySelector('.faq__blocks');
    this.input = this.querySelector('[data-faq-search]');
    this.clearButton = this.querySelector('[data-faq-clear]');
    this.emptyMessage = this.querySelector('[data-faq-empty]');
    this.status = this.querySelector('[data-faq-status]');
    this.pills = Array.from(this.querySelectorAll('[data-faq-term]'));
    this.closeOthers = this.hasAttribute('data-close-others');
    this.emptyTemplate = this.dataset.noResults || '';
    this.statusTemplate = this.dataset.resultsAnnouncement || '';
    this.searchTimer = null;

    this.items = Array.from(this.querySelectorAll('[data-faq-item]')).map((element) => {
      const questionElement = element.querySelector('[data-faq-question]');
      const answerElement = element.querySelector('.faq-item__answer');
      const question = faqCollapseSpace(questionElement ? questionElement.textContent : '');
      const haystack = faqFold(
        faqCollapseSpace(
          [question, answerElement ? answerElement.textContent : '', element.dataset.faqKeywords].join(' ')
        )
      );

      return { element, questionElement, question, haystack };
    });

    this.items.forEach((item) => {
      const summary = item.element.querySelector('summary');
      if (summary) summary.addEventListener('click', this.onSummaryClick.bind(this, item.element));
    });

    if (this.input) {
      this.input.addEventListener('input', this.onSearchInput.bind(this));
      this.input.addEventListener('keydown', this.onSearchKeydown.bind(this));
    }

    if (this.clearButton) this.clearButton.addEventListener('click', this.clearSearch.bind(this));

    this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick.bind(this, pill)));

    // A back navigation can restore a value into the field before this element wakes up.
    if (this.input && this.input.value.trim() !== '') this.applySearch();

    this.openFromHash();
    this.hashListener = this.openFromHash.bind(this);
    window.addEventListener('hashchange', this.hashListener);
  }

  disconnectedCallback() {
    if (this.hashListener) window.removeEventListener('hashchange', this.hashListener);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------------------------------------------------------------- opening */

  onSummaryClick(details, event) {
    event.preventDefault();
    if (details.open) {
      this.close(details);
    } else {
      this.open(details);
    }
  }

  open(details, animate = true) {
    if (this.closeOthers) {
      this.items.forEach((item) => {
        if (item.element !== details && item.element.open) this.close(item.element);
      });
    }

    this.cancelAnimation(details);
    details.open = true;

    const panel = details.querySelector('.faq-item__panel');
    if (!panel || !panel.animate || !animate || this.prefersReducedMotion) return;

    const frames = this.panelKeyframes(panel);
    this.runAnimation(details, panel, [frames.collapsed, frames.expanded]);
  }

  close(details) {
    if (!details.open) return;

    const panel = details.querySelector('.faq-item__panel');
    this.cancelAnimation(details);

    if (!panel || !panel.animate || this.prefersReducedMotion) {
      details.open = false;
      return;
    }

    const frames = this.panelKeyframes(panel);
    const animation = this.runAnimation(details, panel, [frames.expanded, frames.collapsed]);

    animation.addEventListener('finish', () => {
      details.open = false;
    });
  }

  /**
   * The panel carries the answer's bottom spacing, and a border box cannot be shorter than its own
   * padding, so that padding has to collapse alongside the height or the close ends on a jump.
   */
  panelKeyframes(panel) {
    return {
      collapsed: { height: '0px', paddingBottom: '0px', opacity: 0 },
      expanded: {
        height: `${panel.getBoundingClientRect().height}px`,
        paddingBottom: window.getComputedStyle(panel).paddingBottom,
        opacity: 1,
      },
    };
  }

  /** Height is animated on the panel, so it has to be clipped for the length of the animation. */
  runAnimation(details, panel, keyframes) {
    panel.style.overflow = 'hidden';

    const animation = panel.animate(keyframes, {
      duration: FAQ_ANIMATION_DURATION,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });

    const tidy = () => {
      panel.style.overflow = '';
      if (details.faqAnimation === animation) details.faqAnimation = null;
    };

    animation.addEventListener('finish', tidy);
    animation.addEventListener('cancel', tidy);
    details.faqAnimation = animation;

    return animation;
  }

  cancelAnimation(details) {
    if (details.faqAnimation) details.faqAnimation.cancel();
  }

  /* ---------------------------------------------------------------- search */

  onSearchInput() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.applySearch.bind(this), FAQ_SEARCH_DEBOUNCE);
  }

  onSearchKeydown(event) {
    if (event.key !== 'Escape' || this.input.value === '') return;
    event.preventDefault();
    this.clearSearch();
  }

  onPillClick(pill) {
    if (!this.input) return;
    const term = pill.dataset.faqTerm || '';
    // A second press on the active shortcut clears it, which is what a pressed toggle promises.
    this.input.value = faqFold(this.input.value.trim()) === faqFold(term) ? '' : term;
    this.applySearch();
    this.input.focus();
  }

  clearSearch() {
    if (!this.input) return;
    this.input.value = '';
    this.applySearch();
    this.input.focus();
  }

  applySearch() {
    const query = this.input ? faqCollapseSpace(this.input.value) : '';
    const terms = query === '' ? [] : faqFold(query).split(' ');
    let visible = 0;

    this.items.forEach((item) => {
      const matches = terms.every((term) => item.haystack.indexOf(term) !== -1);
      item.element.hidden = !matches;
      if (matches) visible += 1;

      // An item revealed by a search may never have been scrolled past, so it would still be
      // waiting on the reveal animation's observer. Show it now rather than at the next scroll.
      if (matches && terms.length > 0) item.element.classList.remove(FAQ_OFFSCREEN_CLASSNAME);

      this.highlight(item, terms);
    });

    this.updateGroups();

    if (this.clearButton) this.clearButton.hidden = query === '';

    if (this.emptyMessage) {
      this.emptyMessage.hidden = visible > 0 || terms.length === 0;
      this.emptyMessage.textContent = this.emptyTemplate.replace('[term]', query);
    }

    if (this.status) {
      this.status.textContent = terms.length === 0 ? '' : this.statusTemplate.replace('[count]', visible);
    }

    this.pills.forEach((pill) => {
      pill.setAttribute('aria-pressed', faqFold(pill.dataset.faqTerm || '') === faqFold(query) && query !== '');
    });
  }

  highlight(item, terms) {
    if (!item.questionElement) return;

    const ranges = terms.length === 0 ? [] : faqMatchRanges(item.question, terms);
    if (ranges.length === 0) {
      item.questionElement.textContent = item.question;
      return;
    }

    let html = '';
    let cursor = 0;
    ranges.forEach(([start, end]) => {
      html += faqEscape(item.question.slice(cursor, start));
      html += `<mark>${faqEscape(item.question.slice(start, end))}</mark>`;
      cursor = end;
    });
    html += faqEscape(item.question.slice(cursor));

    item.questionElement.innerHTML = html;
  }

  /** A group heading is only meaningful while at least one of its questions is on screen. */
  updateGroups() {
    if (!this.blocks) return;

    let group = null;
    let groupHasVisible = false;

    const settle = () => {
      if (group) group.hidden = !groupHasVisible;
    };

    Array.from(this.blocks.children).forEach((node) => {
      if (node.hasAttribute('data-faq-group')) {
        settle();
        group = node;
        groupHasVisible = false;
      } else if (node.hasAttribute('data-faq-item') && !node.hidden) {
        groupHasVisible = true;
      }
    });

    settle();
  }

  /* ------------------------------------------------------------- deep links */

  openFromHash() {
    const id = window.location.hash.slice(1);
    if (id === '') return;

    const target = document.getElementById(id);
    if (!target || !this.contains(target) || !target.hasAttribute('data-faq-item')) return;

    this.open(target, false);
    target.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}

// Theme editor: selecting a question in the sidebar should open it, so the merchant can see the
// answer they are editing instead of a closed row.
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('[data-faq-item]');
    const accordion = event.target.closest('faq-accordion');
    if (details && accordion) accordion.open(details, false);
  });
}
